/**
 * Batch expiry (1.61) — the rules every screen reads: the till's amber note,
 * the owner's daily window, the stock doctor's list. Pure functions over the
 * store's `stockLots` cache; the database (184) keeps the batches themselves.
 *
 * A batch carries a date and a quantity, never money: a value shown here is
 * qty × the product's average cost, the stock doctor's rule.
 */

import type { Product } from './mock-data'
import { isService } from './service'
import { EXPIRY_SOON, EXPIRY_WINDOW } from './intelligence'

export type StockLot = {
  id: string
  product_id: string
  branch_id: string
  /** null = „ვადა არ მითითებულა" — stock whose pack date nobody gave */
  expiry_date: string | null
  qty: number
  received_qty: number
  received_at: string
}

export type LotRow = { lot: StockLot; name: string; days: number | null; value: number }

const DAY = 86_400_000

/** Whole days from local today to `date` (YYYY-MM-DD); negative once past. */
export function daysLeft(date: string, now = Date.now()): number {
  const today = new Date(now); today.setHours(0, 0, 0, 0)
  return Math.round((new Date(date + 'T00:00:00').getTime() - today.getTime()) / DAY)
}

/** A pack date of today or earlier — almost always a receipt date typed by mistake. */
export const isPastOrToday = (date: string, now = Date.now()) => daysLeft(date, now) <= 0

/** The earliest open expired batch of this product on this branch, or null. */
export function expiredLotFor(productId: string, branchId: string, lots: StockLot[], now = Date.now()): StockLot | null {
  let best: StockLot | null = null
  for (const l of lots) {
    if (l.product_id !== productId || l.branch_id !== branchId || l.qty <= 0 || !l.expiry_date) continue
    if (daysLeft(l.expiry_date, now) >= 0) continue
    if (!best || l.expiry_date < best.expiry_date!) best = l
  }
  return best
}

/** Open dated batches of tracked products expiring within `within` days (or already past). */
export function expiringLots(lots: StockLot[], products: Product[], within = EXPIRY_SOON, now = Date.now()): LotRow[] {
  const byId = new Map(products.map((p) => [p.id, p]))
  const out: LotRow[] = []
  for (const l of lots) {
    if (l.qty <= 0 || !l.expiry_date) continue
    const p = byId.get(l.product_id)
    if (!p || !p.track_expiry || isService(p)) continue
    const days = daysLeft(l.expiry_date, now)
    if (days > within) continue
    out.push({ lot: l, name: p.name, days, value: l.qty * p.purchase_price })
  }
  return out.sort((a, b) => (a.days! - b.days!) || a.name.localeCompare(b.name))
}

/** What the owner's once-a-day window says; null when there is nothing to say. */
export function ownerWindow(lots: StockLot[], products: Product[], now = Date.now()) {
  const rows = expiringLots(lots, products, EXPIRY_WINDOW, now)
  if (rows.length === 0) return null
  const expired = rows.filter((r) => r.days! < 0).length
  return {
    expired,
    soon: rows.length - expired,
    value: rows.reduce((s, r) => s + r.value, 0),
    rows: rows.slice(0, 5),
  }
}

/** localStorage key of the day the window was last shown — per shop, per user. */
export const windowSeenKey = (userId: string, orgId: string) => `martepos.expiryWindow.${orgId}.${userId}`

/** The owner's window: once a calendar day, and only once the batches are loaded and there is something to say —
 *  marking the day „seen" before the data arrived would silently skip it (Codex, design review). */
export const shouldShowWindow = (lastSeen: string | null, today: string, loaded: boolean, hasData: boolean) =>
  loaded && hasData && lastSeen !== today

/**
 * 1.62 — the 1.61 final review's Minor (Codex): two batch reads in flight for one shop (a realtime signal and a
 * sale's refresh) could land out of order, the older page overwriting the newer for one refresh. A read lands only
 * while it is the newest started (`epoch === current`) and the shop is still the one it was read for (`gen`).
 */
export function lotsEpochOk(epoch: number, current: number, gen: number, bootGen: number): boolean {
  return epoch === current && gen === bootGen
}
